// listen_choose — play audio, choose the matching image/word. Same answer shape
// as multiple_choice but the prompt is a (required) audio clip. The audio
// auto-renders a big play button; options are below.

import { useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ActivityImage, ArabicText, AudioButton, ChoiceButton } from './primitives';
import type { ActivityRendererProps } from './types';

type StrippedOption = { id: string; labelKey: string; image?: string | null };
type StrippedListenChoose = {
  audio: string;
  instructionKey?: string | null;
  options: StrippedOption[];
};

export function ListenChoose({
  data,
  ctx,
  cb,
}: ActivityRendererProps<StrippedListenChoose>): ReactElement {
  const { t } = ctx;
  const [chosen, setChosen] = useState<string | null>(null);

  function choose(id: string): void {
    if (cb.locked) return;
    setChosen(id);
    cb.onAnswer({ kind: 'choose', chosenId: id } satisfies content.AttemptInput);
  }

  return (
    <div className="mx-auto flex max-w-md flex-col items-center">
      <p className="text-center text-lg font-semibold text-ink-700">
        {t(data.instructionKey ?? 'content.common.listen_choose')}
      </p>
      <div className="mt-5 scale-125">
        <AudioButton src={data.audio} ctx={ctx} label={t('content.common.play_audio')} />
      </div>

      <div className="mt-8 grid w-full grid-cols-2 gap-3">
        {data.options.map((opt) => {
          const isChosen = chosen === opt.id;
          const state =
            cb.locked && isChosen ? (cb.outcome?.correct ? 'correct' : 'incorrect') : 'idle';
          return (
            <ChoiceButton
              key={opt.id}
              onClick={() => choose(opt.id)}
              selected={isChosen}
              state={state}
              disabled={cb.locked}
            >
              <span className="flex flex-col items-center gap-2">
                {opt.image ? (
                  <span className="h-16 w-16">
                    <ActivityImage
                      src={opt.image}
                      ctx={ctx}
                      alt={t(opt.labelKey)}
                      fallbackEmoji="❓"
                    />
                  </span>
                ) : null}
                <ArabicText text={t(opt.labelKey)} ctx={ctx} />
              </span>
            </ChoiceButton>
          );
        })}
      </div>
    </div>
  );
}
